import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Iconify from "../../iconify/Iconify";

const VISIBILITY_OPTIONS = [
  {
    value: false,
    title: "Intern",
    description: "Nur Team/Admin sieht die Veranstaltung im Dashboard.",
    icon: "solar:shield-warning-bold-duotone",
    color: "#64748b",
  },
  {
    value: true,
    title: "Öffentlich",
    description: "Die Veranstaltung ist im Mitgliederbereich sichtbar.",
    icon: "solar:eye-bold-duotone",
    color: "#15803d",
  },
];

export default function StepVisibility({ formData, onChange }) {
  const isPublic = Boolean(formData.isPublic);

  const handleVisibilityChange = (nextValue) => {
    onChange("isPublic", nextValue);
  };

  const handleSelfRegistrationToggle = (event) => {
    onChange("allowSelfRegistration", event.target.checked);
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">Sichtbarkeit & Anmeldung</Typography>
        <Typography variant="body2" color="text.secondary">
          Legen Sie fest, wer das Event sehen kann und ob Mitglieder sich selbst
          anmelden dürfen.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        {VISIBILITY_OPTIONS.map((option) => {
          const isActive = isPublic === option.value;
          return (
            <Paper
              key={option.title}
              variant="outlined"
              role="button"
              tabIndex={0}
              aria-pressed={isActive}
              onClick={() => handleVisibilityChange(option.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleVisibilityChange(option.value);
                }
              }}
              sx={{
                p: 2,
                borderWidth: isActive ? 2 : 1,
                borderColor: isActive ? option.color : "divider",
                bgcolor: isActive ? alpha(option.color, 0.1) : "background.paper",
                cursor: "pointer",
                transition: "all 160ms ease",
                "&:hover": {
                  borderColor: option.color,
                  bgcolor: alpha(option.color, 0.08),
                },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(option.color, isActive ? 0.5 : 0.3)}`,
                    bgcolor: alpha(option.color, isActive ? 0.18 : 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Iconify icon={option.icon} width={22} sx={{ color: option.color }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {option.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {option.description}
                  </Typography>
                </Box>
                {isActive ? (
                  <Iconify
                    icon="solar:check-circle-bold"
                    width={20}
                    sx={{ color: option.color, mt: 0.25 }}
                  />
                ) : null}
              </Stack>
            </Paper>
          );
        })}
      </Box>

      {isPublic ? (
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(formData.allowSelfRegistration)}
                onChange={handleSelfRegistrationToggle}
                color="secondary"
              />
            }
            label="Mitglieder dürfen sich selbst anmelden"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: -0.5 }}>
            Wenn deaktiviert, können nur Admin, Editor oder Trainer Mitglieder anmelden.
          </Typography>
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Bei internen Events ist die Selbstanmeldung im Mitgliederbereich nicht relevant.
        </Typography>
      )}

      <TextField
        label="Passwort (optional)"
        type="text"
        value={formData.password || ""}
        onChange={(event) => onChange("password", event.target.value)}
        fullWidth
        helperText="Optionaler Zugangsschutz für die Event-Teilnahme."
      />
    </Stack>
  );
}

StepVisibility.propTypes = {
  formData: PropTypes.shape({
    isPublic: PropTypes.bool,
    allowSelfRegistration: PropTypes.bool,
    password: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};
